// pages/IdiomasCorreos.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Card,
  Divider,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ExportOutlined,
  CopyOutlined,
  HomeOutlined,
  MailOutlined,
  TranslationOutlined
} from '@ant-design/icons';
import axios from 'axios';
import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';
import './IdiomasCorreos.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const IdiomasCorreos = () => {
  // Estados
  const [textos, setTextos] = useState([]);
  const [hoteles, setHoteles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentTexto, setCurrentTexto] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copySourceHotel, setCopySourceHotel] = useState(null);
  const [copyTargetHotel, setCopyTargetHotel] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);

  // Campos disponibles
  const camposCorreo = [
    { value: 'FirmaCorreo', label: 'Firma de Correo', icon: <MailOutlined />, description: 'Firma que aparece al final de los correos' },
    { value: 'AsuntoReserva', label: 'Asunto Reserva', icon: <MailOutlined />, description: 'Asunto del correo de confirmación' },
    { value: 'CuerpoReserva', label: 'Cuerpo Reserva', icon: <FileTextOutlined />, description: 'Cuerpo del correo de confirmación' },
    { value: 'AsuntoCancelacion', label: 'Asunto Cancelación', icon: <MailOutlined />, description: 'Asunto del correo de cancelación' },
    { value: 'CuerpoCancelacion', label: 'Cuerpo Cancelación', icon: <FileTextOutlined />, description: 'Cuerpo del correo de cancelación' },
    { value: 'AsuntoModificacion', label: 'Asunto Modificación', icon: <MailOutlined />, description: 'Asunto del correo de modificación' },
    { value: 'CuerpoModificacion', label: 'Cuerpo Modificación', icon: <FileTextOutlined />, description: 'Cuerpo del correo de modificación' }
  ];

const idiomas = [
  { value: 'CAS', label: 'Español', flagCode: 'es' },
  { value: 'CAT', label: 'Catalán', flagCode: 'es-ct' },
  { value: 'ESP', label: 'Español (ESP)', flagCode: 'es' },
  { value: 'FRA', label: 'Francés', flagCode: 'fr' },
  { value: 'ING', label: 'Inglés', flagCode: 'gb' },
  { value: 'GER', label: 'Alemán', flagCode: 'de' },
  { value: 'ITA', label: 'Italiano', flagCode: 'it' }
];

  // Columnas de la tabla
  const columns = [
    {
      title: 'Hotel',
      dataIndex: 'CodigoHotel',
      key: 'CodigoHotel',
      width: 120,
      sorter: (a, b) => (a.CodigoHotel || 0) - (b.CodigoHotel || 0),
      sortOrder: sortedInfo.columnKey === 'CodigoHotel' && sortedInfo.order,
      render: (codigo) => {
        const hotel = hoteles.find(h => h.codigo === codigo);
        return (
          <Tag color="blue" style={{ fontWeight: 'bold' }}>
            <HomeOutlined style={{ marginRight: 4 }} />
            {hotel ? hotel.nombre : codigo}
          </Tag>
        );
      },
      filters: [],
      filteredValue: filteredInfo.CodigoHotel || null,
      onFilter: (value, record) => record.CodigoHotel === value,
    },
    {
      title: 'Campo',
      dataIndex: 'Campo',
      key: 'Campo',
      width: 180,
      sorter: (a, b) => (a.Campo || '').localeCompare(b.Campo || ''),
      sortOrder: sortedInfo.columnKey === 'Campo' && sortedInfo.order,
      render: (campo) => {
        const campoInfo = camposCorreo.find(c => c.value === campo);
        return (
          <Tag color="purple" icon={campoInfo?.icon || <MailOutlined />}>
            {campoInfo?.label || campo}
          </Tag>
        );
      },
      filters: camposCorreo.map(c => ({ text: c.label, value: c.value })),
      filteredValue: filteredInfo.Campo || null,
      onFilter: (value, record) => record.Campo === value,
    },
{
  title: 'Idioma',
  dataIndex: 'Idioma',
  key: 'Idioma',
  width: 100,
  sorter: (a, b) => (a.Idioma || '').localeCompare(b.Idioma || ''),
  sortOrder: sortedInfo.columnKey === 'Idioma' && sortedInfo.order,
  render: (idioma) => {
    const langInfo = idiomas.find(l => l.value === idioma);
    return (
      <Tag color="green">
        <span 
          className={`fi fi-${langInfo?.flagCode}`} 
          style={{ marginRight: 6 }}
        />
        {langInfo?.label || idioma}
      </Tag>
    );
  },
  filters: idiomas.map(l => ({ text: l.label, value: l.value })),
  filteredValue: filteredInfo.Idioma || null,
  onFilter: (value, record) => record.Idioma === value,
},
    {
      title: 'Contenido',
      dataIndex: 'Texto',
      key: 'Texto',
      width: 350,
      ellipsis: true,
      render: (texto) => (
        <Tooltip title={texto ? (texto.length > 200 ? texto.substring(0, 300) + '...' : texto) : 'Sin contenido'}>
          <div style={{ maxWidth: 330 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {texto ? (texto.length > 100 ? texto.substring(0, 100) + '...' : texto) : 'Sin contenido'}
            </Text>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar texto de correo?"
            description="Esta acción eliminará el registro permanentemente"
            onConfirm={() => handleDelete(record)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchHoteles();
    fetchTextos();
  }, []);

  // Actualizar filtros de hoteles cuando cambian
  useEffect(() => {
    if (hoteles.length > 0) {
      const hotelFilters = hoteles.map(h => ({ text: h.nombre, value: h.codigo }));
      columns[0].filters = hotelFilters;
    }
  }, [hoteles]);

  const fetchHoteles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/hoteles/listaHoteles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHoteles(response.data.hoteles || []);
    } catch (error) {
      console.error('❌ Error cargando hoteles:', error);
      message.error('Error al cargar los hoteles');
    }
  };

  const fetchTextos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {};
      
      if (searchText) {
        queryParams.search = searchText;
      }
      
      if (filteredInfo.CodigoHotel) {
        queryParams.CodigoHotel = filteredInfo.CodigoHotel[0];
      }
      if (filteredInfo.Campo) {
        queryParams.Campo = filteredInfo.Campo[0];
      }
      if (filteredInfo.Idioma) {
        queryParams.Idioma = filteredInfo.Idioma[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/idiomas_correos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Texto: item.Texto || ''
      }));
      
      setTextos(cleanedData);
      
    } catch (error) {
      console.error('❌ Error cargando textos de correo:', error);
      message.error('Error al cargar los textos de correo');
      setTextos([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    fetchTextos();
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentTexto(null);
    form.resetFields();
    form.setFieldsValue({
      Idioma: 'CAS'
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentTexto(record);
    form.setFieldsValue({
      CodigoHotel: record.CodigoHotel,
      Idioma: record.Idioma,
      Campo: record.Campo,
      Texto: record.Texto || ''
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    const hotel = hoteles.find(h => h.codigo === record.CodigoHotel);
    const campoInfo = camposCorreo.find(c => c.value === record.Campo);
    const langInfo = idiomas.find(l => l.value === record.Idioma);
    
    Modal.info({
      title: `Detalle del Texto de Correo`,
      width: 800,
      icon: <MailOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <strong>Hotel:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  <HomeOutlined /> {hotel ? hotel.nombre : record.CodigoHotel}
                </Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Campo:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {campoInfo?.icon} {campoInfo?.label || record.Campo}
                </Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Idioma:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {langInfo?.flag} {langInfo?.label || record.Idioma}
                </Tag>
              </div>
            </Col>
            <Col span={24}>
              <Divider />
              <strong>Contenido del texto:</strong>
              <div style={{ 
                marginTop: 8, 
                padding: '16px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {record.Texto ? (
                  <div dangerouslySetInnerHTML={{ __html: record.Texto }} />
                ) : (
                  <Text type="secondary">Sin contenido</Text>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (record) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/idiomas_correos/${record.CodigoHotel}/${record.Idioma}/${record.Campo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTextos(textos.filter(t => 
        !(t.CodigoHotel === record.CodigoHotel && 
          t.Idioma === record.Idioma && 
          t.Campo === record.Campo)
      ));
      
      message.success('Texto de correo eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando texto de correo:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el texto de correo');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/idiomas_correos`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Texto de correo creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/idiomas_correos/${currentTexto.CodigoHotel}/${currentTexto.Idioma}/${currentTexto.Campo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Texto de correo actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchTextos();
      
    } catch (error) {
      console.error('❌ Error guardando texto de correo:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleOpenCopyModal = () => {
    setCopySourceHotel(null);
    setCopyTargetHotel(null);
    setCopyModalVisible(true);
  };

  const handleCopyTexts = async () => {
    if (!copySourceHotel || !copyTargetHotel) {
      message.warning('Por favor seleccione hotel origen y destino');
      return;
    }

    if (copySourceHotel === copyTargetHotel) {
      message.warning('El hotel origen y destino no pueden ser el mismo');
      return;
    }

    setCopyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/idiomas_correos/copy`,
        {
          sourceHotel: copySourceHotel,
          targetHotel: copyTargetHotel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(`Se copiaron ${response.data.copiedCount || 0} registros correctamente`);
      setCopyModalVisible(false);
      fetchTextos();
    } catch (error) {
      console.error('❌ Error copiando textos:', error);
      message.error(error.response?.data?.error || 'Error al copiar los textos de correo');
    } finally {
      setCopyLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    fetchTextos();
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchTextos();
  };

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  return (
    <>
      <PageContainer
        title="Idiomas y Textos de Correos"
        icon={<TranslationOutlined />}
        loading={loading}
        onRefresh={() => fetchTextos()}
        onCreate={handleCreate}
        badgeCount={textos.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={handleOpenCopyModal}
            >
              Copiar textos entre hoteles
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >        
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="Buscar por hotel, idioma, campo o contenido..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 350 }}
              onPressEnter={handleSearch}
              allowClear
              onClear={handleResetSearch}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={searchLoading}
            >
              Buscar
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetSearch}
            >
              Restablecer
            </Button>
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={textos}
          rowKey={(record) => `${record.CodigoHotel}-${record.Idioma}-${record.Campo}`}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1100, y: 'calc(100vh - 350px)' }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          pagination={false}
          locale={{
            emptyText: 'No se encontraron textos de correo'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar texto de correo */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Texto de Correo' : 'Editar Texto de Correo'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={850}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Idioma: 'CAS'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="CodigoHotel"
                label="Hotel"
                rules={[{ required: true, message: 'Por favor seleccione un hotel' }]}
              >
                <Select
                  placeholder="Seleccione un hotel"
                  showSearch
                  optionFilterProp="children"
                  disabled={modalType === 'edit'}
                >
                  {hoteles.map(hotel => (
                    <Option key={hotel.codigo} value={hotel.codigo}>
                      <HomeOutlined /> {hotel.codigo} - {hotel.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Campo"
                label="Campo / Tipo de Correo"
                rules={[{ required: true, message: 'Por favor seleccione un campo' }]}
              >
                <Select
                  placeholder="Seleccione un campo"
                  showSearch
                  optionFilterProp="children"
                  disabled={modalType === 'edit'}
                >
                  {camposCorreo.map(campo => (
                    <Option key={campo.value} value={campo.value}>
                      {campo.icon} {campo.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Idioma"
                label="Idioma"
                rules={[{ required: true, message: 'Por favor seleccione un idioma' }]}
              >
                <Select
                  placeholder="Seleccione un idioma"
                  disabled={modalType === 'edit'}
                >
                  {idiomas.map(idioma => (
                    <Option key={idioma.value} value={idioma.value}>
                      {idioma.flag} {idioma.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="Texto"
            label="Contenido del Texto"
            tooltip="Puede usar HTML para dar formato al texto. Variables disponibles: ###NombreReserva###, ###Hotel###, ###FechaEntrada###, ###FechaSalida###, etc."
          >
              <Input.TextArea
              rows={15}
              placeholder="Escriba el contenido aquí... Puede usar etiquetas HTML para formato"
              showCount
              maxLength={50000}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </Form.Item>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Variables disponibles:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>###NombreReserva###</strong> - Nombre del cliente</li>
                  <li><strong>###Hotel###</strong> - Nombre del hotel</li>
                  <li><strong>###FechaEntrada###</strong> - Fecha de entrada</li>
                  <li><strong>###FechaSalida###</strong> - Fecha de salida</li>
                  <li><strong>###Localizador###</strong> - Número de localizador</li>
                  <li><strong>###URL###</strong> - Enlace de confirmación</li>
                </ul>
                <div style={{ marginTop: 8 }}>
                  <strong>📌 Campos disponibles:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    {camposCorreo.map(campo => (
                      <li key={campo.value}><strong>{campo.value}</strong> - {campo.description}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </Form>
      </Modal>

      {/* Modal para copiar textos entre hoteles */}
      <Modal
        title="Copiar Textos de Correo entre Hoteles"
        open={copyModalVisible}
        onOk={handleCopyTexts}
        onCancel={() => setCopyModalVisible(false)}
        width={500}
        okText="Copiar"
        cancelText="Cancelar"
        confirmLoading={copyLoading}
      >
        <Alert
          message="Información"
          description="Esta acción copiará TODOS los textos de correo del hotel origen al hotel destino. Los textos existentes en el hotel destino serán reemplazados."
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />
        
        <Form layout="vertical">
          <Form.Item label="Hotel Origen" required>
            <Select
              placeholder="Seleccione el hotel de origen"
              showSearch
              optionFilterProp="children"
              value={copySourceHotel}
              onChange={setCopySourceHotel}
            >
              {hoteles.map(hotel => (
                <Option key={hotel.codigo} value={hotel.codigo}>
                  <HomeOutlined /> {hotel.codigo} - {hotel.nombre}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Hotel Destino" required>
            <Select
              placeholder="Seleccione el hotel de destino"
              showSearch
              optionFilterProp="children"
              value={copyTargetHotel}
              onChange={setCopyTargetHotel}
            >
              {hoteles.map(hotel => (
                <Option key={hotel.codigo} value={hotel.codigo}>
                  <HomeOutlined /> {hotel.codigo} - {hotel.nombre}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default IdiomasCorreos;